import { useMutation, useQuery } from '@tanstack/react-query';
import { Form } from 'antd';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Filter } from '../../../../@types/filter';
import { departmentApi } from '../../../../apis';
import { CreateDepartmentDto, Department, UpdateDepartmentDto } from '../../../../apis/client-axios';
import ButtonWrap from '../../../../components/ButtonWrap';
import InputWrap from '../../../../components/InputWrap';
import ModalWrap from '../../../../components/ModalWrap';
import SvgAdd from '../../../../components/svg/SvgAdd';
import SvgDelete from '../../../../components/svg/SvgDelete';
import SvgEdit from '../../../../components/svg/SvgEdit';
import TableWrap from '../../../../components/TableWrap';
import ToastError from '../../../../components/Toast/ToastError';
import ToastSuccess from '../../../../components/Toast/ToastSuccess';
import Widget from '../../../../components/Widget';
import { QUERY_KEY } from '../../../../constants/QueryKey';
import './style.scss';

const DepartmentManagement = () => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<Department | null>(null);
  const [filter, setFilter] = useState<Filter>({ page: 1, pageSize: 20, fullTextSearch: '' });

  const {
    data: dataDepartment,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: [QUERY_KEY.DEPARTMENT_MANAGEMENT, filter],
    queryFn: () => {
      return departmentApi.departmentControllerFindAll(
        filter.page,
        filter.pageSize,
        undefined,
        filter.fullTextSearch,
        undefined,
      );
    },
  });

  const addMutation = useMutation({
    mutationFn: (payload: CreateDepartmentDto) => departmentApi.departmentControllerCreate(payload),
    onSuccess: () => {
      setOpenAddModal(false);
      refetch();
      form.resetFields();
      ToastSuccess({
        content: intl.formatMessage({ id: 'departmentManagement.addSuccees' }),
      });
    },
    onError: (error: { response: { data: { message: string; errorCode: string }; status: number } }) => {
      if (error.response.data.errorCode.includes('EXISTED')) {
        ToastError({
          content: intl.formatMessage({ id: 'departmentManagement.beenCreated' }),
        });
      }
    },
  });

  const editMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateDepartmentDto }) =>
      departmentApi.departmentControllerUpdate(id, payload),
    onSuccess: () => {
      setOpenEditModal(false);
      refetch();
      ToastSuccess({
        content: intl.formatMessage({ id: 'departmentManagement.editSuccees' }),
      });
    },
    onError: (error: { response: { data: { message: string; errorCode: string }; status: number } }) => {
      if (error.response.data.errorCode.includes('EXISTED')) {
        ToastError({
          content: intl.formatMessage({ id: 'departmentManagement.beenCreated' }),
        });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => departmentApi.departmentControllerRemove(id),
    onSuccess: () => {
      setFilter((prevFilter) => ({
        ...prevFilter,
        page: 1,
      }));
      setOpenDeleteModal(false);
      refetch();
      ToastSuccess({
        content: intl.formatMessage({ id: 'departmentManagement.deleteSuccees' }),
      });
    },
    onError: () => {
      ToastError({
        content: intl.formatMessage({ id: 'departmentManagement.deleteFail' }),
      });
    },
  });

  const handleAddDepartment = () => {
    form
      .validateFields()
      .then((values) => {
        const payload: CreateDepartmentDto = {
          name: form.getFieldValue('addDepartment'),
        };
        addMutation.mutate(payload);
      })
      .catch((error) => {
        console.error('Validate Failed:', error);
      });
  };

  const handleEditDepartment = () => {
    form
      .validateFields()
      .then((values) => {
        const payload: UpdateDepartmentDto = {
          name: form.getFieldValue('editDepartment'),
        };
        editMutation.mutate({ id: selectedRowData?.id as string, payload });
      })
      .catch((error) => {
        console.error('Validate Failed:', error);
      });
  };

  const handleDeleteDepartment = () => {
    deleteMutation.mutate(selectedRowData?.id as string);
  };

  const columns = [
    {
      title: intl.formatMessage({ id: 'departmentManagement.title' }),
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => {
        return (
          <div className="department-management-content-name">
            <p className="department-management-content-name-tool">{record?.name}</p>
          </div>
        );
      },
    },
    {
      title: '',
      key: 'action',
      render: (record, row) => (
        <div className="department-management-content-data-options">
          <ButtonWrap
            className="department-management-content-button-edit"
            onClick={() => {
              setOpenEditModal(true);
              setSelectedRowData(row);
              form.setFieldValue('editDepartment', row.name);
            }}
          >
            <SvgEdit /> {intl.formatMessage({ id: 'departmentManagement.editButton' })}
          </ButtonWrap>
          <ButtonWrap
            className="department-management-content-button-delete"
            onClick={() => {
              setOpenDeleteModal(true);
              setSelectedRowData(row);
            }}
          >
            <SvgDelete />
            {intl.formatMessage({ id: 'departmentManagement.deleteButton' })}
          </ButtonWrap>
        </div>
      ),
    },
  ];
  return (
    <div className="department-management">
      <div className="department-management-content">
        <Widget
          breadcrumbs={[
            intl.formatMessage({ id: 'adminMenu.staffDepartmentPosition' }),
            intl.formatMessage({ id: 'departmentManagement.title' }),
          ]}
          content={
            <TableWrap
              isPagination
              loading={isLoading}
              onSearch={(search) => {
                setFilter((prevFilters) => ({
                  ...prevFilters,
                  page: 1,
                  fullTextSearch: search,
                }));
              }}
              paginationProps={{
                current: dataDepartment?.data?.data?.page || 1,
                pageSize: dataDepartment?.data?.data?.size || 20,
                total: dataDepartment?.data?.data?.total || 0,
                onChange: (page, pageSize) =>
                  setFilter((prevFilters) => ({
                    ...prevFilters,
                    page,
                    pageSize,
                  })),
              }}
              inputProps={{
                placeholder: intl.formatMessage({ id: 'departmentManagement.searchBox' }),
              }}
              tableProps={{
                dataSource: Array.isArray(dataDepartment?.data?.data?.content)
                  ? dataDepartment?.data?.data?.content
                  : [],
                columns,
                className: 'department-management-content-table',
              }}
              rightTopContent={
                <>
                  <ButtonWrap
                    onClick={() => {
                      setOpenAddModal(true);
                    }}
                    variant="outlined"
                    className="department-management-content-button-add"
                  >
                    <SvgAdd /> {intl.formatMessage({ id: 'departmentManagement.addButton' })}
                  </ButtonWrap>
                </>
              }
            />
          }
          title={intl.formatMessage({ id: 'departmentManagement.title' })}
        />
      </div>
      <div className="department-management-content-modal">
        {openAddModal && (
          <ModalWrap
            open={openAddModal}
            cancelText={intl.formatMessage({ id: 'departmentManagement.modal.cancel' })}
            submitText={intl.formatMessage({ id: 'departmentManagement.addButton' })}
            buttonSubmitProps={{ disabled: addMutation.isPending }}
            onSubmit={handleAddDepartment}
            onCancel={() => {
              setOpenAddModal(false);
              form.resetFields();
            }}
            type="upsert"
            title={intl.formatMessage({ id: 'departmentManagement.addButtonTitle' })}
            className="department-management-content-modal-add"
            body={
              <Form form={form} layout="vertical">
                <p className="department-management-content-title">
                  {intl.formatMessage({ id: 'departmentManagement.title' })}
                  <span className="label_required">*</span>
                </p>
                <Form.Item
                  name="addDepartment"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({ id: 'common.required' }),
                    },
                    {
                      whitespace: true,
                      message: intl.formatMessage({ id: 'departmentManagement.invalid' }),
                    },
                  ]}
                >
                  <InputWrap
                    className="department-management-content-modal-add-input"
                    placeholder={intl.formatMessage({ id: 'departmentManagement.inputPlaceholder' })}
                    required={true}
                    maxLength={255}
                  />
                </Form.Item>
              </Form>
            }
          />
        )}
      </div>
      <div className="department-management-content-modal">
        {openEditModal && (
          <ModalWrap
            open={openEditModal}
            cancelText={intl.formatMessage({ id: 'departmentManagement.modal.cancel' })}
            submitText={intl.formatMessage({ id: 'departmentManagement.editButton' })}
            buttonSubmitProps={{ disabled: editMutation.isPending }}
            onSubmit={handleEditDepartment}
            onCancel={() => {
              setOpenEditModal(false);
              form.resetFields();
            }}
            type="upsert"
            title={intl.formatMessage({ id: 'departmentManagement.editButtonTitle' })}
            className="department-management-content-modal-add"
            body={
              <Form form={form} layout="vertical">
                <p className="department-management-content-title">
                  {intl.formatMessage({ id: 'departmentManagement.title' })}
                  <span className="label_required">*</span>
                </p>
                <Form.Item
                  name="editDepartment"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({ id: 'common.required' }),
                    },
                    {
                      whitespace: true,
                      message: intl.formatMessage({ id: 'departmentManagement.invalid' }),
                    },
                  ]}
                >
                  <InputWrap
                    className="department-management-content-modal-add-input"
                    placeholder={intl.formatMessage({ id: 'departmentManagement.inputPlaceholder' })}
                    required={true}
                    maxLength={255}
                  />
                </Form.Item>
              </Form>
            }
          />
        )}
      </div>
      <div className="department-management-content-modal">
        {openDeleteModal && (
          <ModalWrap
            open={openDeleteModal}
            cancelText={intl.formatMessage({ id: 'departmentManagement.modal.cancel' })}
            submitText={intl.formatMessage({ id: 'departmentManagement.deleteButton' })}
            buttonSubmitProps={{ disabled: deleteMutation.isPending }}
            onSubmit={handleDeleteDepartment}
            onCancel={() => setOpenDeleteModal(false)}
            type="upsert"
            title={intl.formatMessage({ id: 'departmentManagement.deleteButtonTitle' })}
            className="department-management-content-modal-delete"
            body={
              <div className="department-management-content-modal-delete-body">
                <p className="department-management-content-modal-delete-title">
                  <span className="department-management-content-name-tool name">{selectedRowData?.name}</span>
                  {intl.formatMessage({ id: 'booking.modal.delete' })}
                </p>
              </div>
            }
          />
        )}
      </div>
    </div>
  );
};

export default DepartmentManagement;
